/**
 * Reusable address input using Google Places Autocomplete.
 * - If Google Places JS API is loaded, shows a search input with live suggestions
 *   that auto-fills street + city + state + zip on selection.
 * - If the key/API is missing, gracefully falls back to a plain text input.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2 } from 'lucide-react';
import { ensureGooglePlacesLoaded } from '@/lib/googlePlaces';

export default function AddressAutocomplete({
  value,
  onChange,             // (full_address_text) => void
  onPlaceSelected,      // ({street, city, state, zip_code, formatted, lat, lng}) => void
  label = 'Property Address',
  required = false,
  testId = 'address-autocomplete-input',
  placeholder = 'Start typing your address…',
  countryRestrict = 'us',
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'unavailable'

  useEffect(() => {
    let cancelled = false;
    ensureGooglePlacesLoaded()
      .then(() => {
        if (cancelled) return;
        if (!window.google?.maps?.places || !inputRef.current) {
          setStatus('unavailable');
          return;
        }
        try {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['address'],
            componentRestrictions: countryRestrict ? { country: countryRestrict } : undefined,
            fields: ['address_components', 'formatted_address', 'geometry'],
          });
          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (!place || !place.address_components) return;
            const parsed = parseAddressComponents(place.address_components);
            const formatted = place.formatted_address || '';
            const lat = place.geometry?.location?.lat?.();
            const lng = place.geometry?.location?.lng?.();
            if (onChange) onChange(formatted);
            if (onPlaceSelected) onPlaceSelected({ ...parsed, formatted, lat, lng });
          });
          setStatus('ready');
        } catch {
          setStatus('unavailable');
        }
      })
      .catch(() => { if (!cancelled) setStatus('unavailable'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-2 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="pl-9 h-12"
          data-testid={testId}
          autoComplete="off"
        />
        {status === 'loading' && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>
      {status === 'unavailable' && (
        <p className="mt-1 text-xs text-slate-500">
          Address autocomplete unavailable — type your full address manually.
        </p>
      )}
    </div>
  );
}

function parseAddressComponents(components) {
  const get = (type, useShort = false) => {
    const c = components.find((x) => x.types?.includes(type));
    return c ? (useShort ? c.short_name : c.long_name) : '';
  };
  const streetNumber = get('street_number');
  const route = get('route');
  const street = [streetNumber, route].filter(Boolean).join(' ');
  const city =
    get('locality') || get('postal_town') || get('sublocality_level_1') || get('administrative_area_level_2');
  const state = get('administrative_area_level_1', true);
  const zip_code = get('postal_code');
  const country = get('country', true);
  return { street, city, state, zip_code, country };
}
